/**
 * Roster data hooks — the board's read/write seam on `/shifts` and `/teams`.
 *
 * What this used to be: four hooks on
 * `/sessions/{id}/roster/{team,coverage,rules,commands}`, over an event-sourced
 * `shift_commands` log folded out of a chat transcript. None of those four
 * routes has existed since 2 September 2026, so the roster app 404'd on every
 * one of them. They are not repointed at a stub; they are gone, and the reads
 * they stood for are answered by the real resources:
 *
 *   roster/team     -> GET /teams/{team_id}/members   (a team is PEOPLE)
 *   roster/commands -> POST /shifts/{id}:cancel
 *
 * Writes here are SERVER-AUTHORITATIVE, not optimistic. A shift rewrites ACL
 * grants (who may read which patient), so showing a provisional assignment
 * before the server confirms would mislead a lead about who holds access —
 * the same rule shifts.ts and clinical documents already follow.
 */
import { useMutation, useQueryClient } from '@tanstack/react-query'

import { api } from '@/api/client'
import type { ApiError } from '@/api/client'
import { listShifts, type ListShiftsParams, type ShiftResponse } from '@/api/endpoints/shifts'
import {
  listOccurrences,
  listPlans,
  OPEN_PLAN_STATUSES,
  publishPlan,
  revokePlan,
  type Occurrence,
  type Plan,
} from '@/api/endpoints/plans'
import {
  ROSTER_CATEGORY,
  ROSTER_SUBJECT_TYPE,
  type RosterPlan,
} from '@/api/endpoints/rosterPlan'
import { listTeams, type ListTeamsParams, type TeamResponse } from '@/api/endpoints/teams'
import type { Page } from '@/api/types'
import type { Reference } from '@/lib/reference'
import { useAuthenticatedQuery } from '@/hooks/useAuthenticatedQuery'

/**
 * One person's membership of one team (FHIR PractitionerRole is the shape).
 *
 * `user` is the addressable id; `user_ref` is the same person as the
 * house-standard Reference and is what the board renders. `user_ref.display` is
 * ABSENT when the user row carries no name — render the id, never a guess.
 */
export interface TeamMemberResponse {
  id: string
  team_id: string
  user: string
  user_ref: Reference
  role: string
  /** A code from `config.staffing.qualifications`. Empty = unmapped grade. */
  qualification: string
  fte: number
  period: { start: string; end: string | null }
}

/** One page of memberships, in the shared envelope. */
export type TeamMemberListResponse = Page<TeamMemberResponse>

export const rosterKeys = {
  all: ['roster'] as const,
  teams: (params: ListTeamsParams) => ['roster', 'teams', params] as const,
  members: (teamId: string) => ['roster', 'members', teamId] as const,
  shifts: (params: ListShiftsParams) => ['roster', 'shifts', params] as const,
  plan: (wardId: string) => ['roster', 'plan', wardId] as const,
  /** What was actually worked, for one plan over the period it covers. */
  occurrences: (planId: string, from: string, to: string) =>
    ['roster', 'occurrences', planId, from, to] as const,
}

/**
 * What was actually worked on this roster, per item.
 *
 * EXECUTION IS THE PLANNER'S. A shift is the occurrence of a roster item, and
 * whether it was started, completed, cancelled or missed is folded out of the
 * plan's own change log — care-app keeps no copy. This is also the only way a
 * board learns what to DRAW: the shift type's name, colour and window are
 * resolved from the category's declaration, which the platform does not serve.
 *
 * The window is the plan's own period, so one read covers the whole board.
 * A plan not yet in force has no occurrences and the planner answers an empty
 * page, which is why a draft's cells carry their bare code.
 */
export function useRosterOccurrences(plan: RosterPlan | null | undefined) {
  const labels = plan?.attributes?.period?.day_labels ?? []
  const from = labels[0] ?? ''
  const to = labels[labels.length - 1] ?? ''
  const query = useAuthenticatedQuery<Map<string, Occurrence>>({
    queryKey: rosterKeys.occurrences(plan?.id ?? 'none', from, to),
    queryFn: async () => {
      const page = await listOccurrences(plan as RosterPlan, from, to)
      const byItem = new Map<string, Occurrence>()
      for (const occurrence of page.items) byItem.set(occurrence.item_id, occurrence)
      return byItem
    },
    // A period with no days is a plan that cannot be drawn, not a read to make.
    enabled: !!plan && from !== '' && to !== '',
  })
  // A FAILED READ IS NOT AN EMPTY ROSTER. In flight and "nothing recorded" draw
  // alike, which is honest; a refusal is not, because every cell would fall
  // back to a bare code and look like an unpublished plan. The error travels.
  return { occurrences: query.data ?? EMPTY_OCCURRENCES, error: query.error }
}

const EMPTY_OCCURRENCES: ReadonlyMap<string, Occurrence> = new Map()

/**
 * The teams the caller may view — their own, and any they lead. The board needs
 * one before it can ask anything: a shift belongs to a TEAM's roster, so
 * "which roster" is a question with an answer, not a default.
 */
export function useRosterTeams(params: ListTeamsParams = {}, options?: { enabled?: boolean }) {
  return useAuthenticatedQuery<TeamResponse[], ApiError>({
    queryKey: rosterKeys.teams(params),
    queryFn: () => listTeams(params),
    enabled: options?.enabled ?? true,
    refetchOnWindowFocus: false,
  })
}

/**
 * The people a roster may be planned from. Every member is a board ROW, even
 * one with no shift in the window — an unscheduled nurse must still be visible,
 * and the empty state must show the real team rather than a
 * blank canvas.
 */
export function useRosterTeamMembers(teamId: string | undefined, options?: { enabled?: boolean }) {
  return useAuthenticatedQuery<TeamMemberResponse[], ApiError>({
    queryKey: rosterKeys.members(teamId ?? ''),
    queryFn: () => api.getAllItems<TeamMemberListResponse>(`/teams/${teamId}/members`),
    enabled: !!teamId && (options?.enabled ?? true),
    refetchOnWindowFocus: false,
  })
}

/**
 * The team's shifts over a window.
 *
 * The window is expressed on the TIMESTAMPS, never on `date`: `date` is the
 * roster-day label and a night shift labelled the 14th is worked into the 15th,
 * so a label comparison would drop the very shift a lead is asking about.
 * `planned_end > from AND planned_start < to` is the overlap test — a shift that
 * starts before the window but is still being worked inside it belongs on the
 * board.
 */
export function useRosterShifts(
  window: { teamId?: string; from?: string; to?: string },
  options?: { enabled?: boolean },
) {
  const clauses: string[] = []
  if (window.teamId) clauses.push(`team = ${window.teamId}`)
  if (window.from) clauses.push(`planned_end > "${window.from}"`)
  if (window.to) clauses.push(`planned_start < "${window.to}"`)
  const params: ListShiftsParams = clauses.length > 0 ? { filter: clauses.join(' AND ') } : {}

  return useAuthenticatedQuery<ShiftResponse[], ApiError>({
    queryKey: rosterKeys.shifts(params),
    queryFn: () => listShifts(params),
    enabled: !!window.teamId && (options?.enabled ?? true),
    refetchOnWindowFocus: false,
  })
}

/** Invalidate every roster read after a write; the server is the truth. */
function invalidateRoster(queryClient: ReturnType<typeof useQueryClient>) {
  void queryClient.invalidateQueries({ queryKey: rosterKeys.all })
}

/**
 * Take one shift off the roster — `POST /shifts/{id}:cancel`.
 *
 * Cancel, not delete: a published shift is a fact the ward acted on, and
 * `:batchDelete` refuses anything that is not a draft precisely so a roster
 * cannot be quietly erased. Cancelling leaves the record and the reason
 * visible, which is what the audit trail is for.
 */
export function useCancelRosterShift() {
  const queryClient = useQueryClient()
  return useMutation<ShiftResponse, ApiError, string>({
    mutationFn: (shiftId) => api.post<ShiftResponse>(`/shifts/${shiftId}:cancel`, {}),
    onSuccess: () => invalidateRoster(queryClient),
  })
}

/**
 * The ward's roster plan — the proposal a lead is looking at before it is the
 * ward's answer.
 *
 * Found by its SUBJECT, because the board knows the ward and the plan's id is
 * the planning service's. An empty list means nobody has planned this ward yet,
 * which is a reason to start rather than an error, so null travels.
 *
 * `completed` and `revoked` are final, so the read asks for the open statuses
 * and the service answers them newest first. The board opens on the first
 * element; nothing here filters or sorts.
 */
export function useRosterPlan(wardId: string | undefined) {
  return useAuthenticatedQuery<RosterPlan | null>({
    queryKey: rosterKeys.plan(wardId ?? 'none'),
    queryFn: async () => {
      const page = await listPlans(
        { type: ROSTER_SUBJECT_TYPE, id: wardId as string },
        ROSTER_CATEGORY,
        OPEN_PLAN_STATUSES,
      )
      return page.items.length === 0 ? null : (page.items[0] as RosterPlan)
    },
    enabled: !!wardId,
  })
}

/**
 * Publish the plan — `POST /plans/{category}/{id}:publish`, so the version a lead is
 * looking at becomes the one in force.
 */
export function usePublishRosterPlan() {
  const queryClient = useQueryClient()
  return useMutation<Plan, ApiError, { planId: string; version: number }>({
    mutationFn: ({ planId, version }) => publishPlan({ category: ROSTER_CATEGORY, id: planId }, version),
    onSuccess: () => invalidateRoster(queryClient),
  })
}

/**
 * Withdraw the plan — `POST /plans/{category}/{id}:revoke`. It is not how a plan is
 * corrected: a correction is a replace, and revoking is what a lead does to a
 * plan nobody wants.
 */
export function useRevokeRosterPlan() {
  const queryClient = useQueryClient()
  return useMutation<Plan, ApiError, { planId: string; version: number }>({
    mutationFn: ({ planId, version }) => revokePlan({ category: ROSTER_CATEGORY, id: planId }, version),
    onSuccess: () => invalidateRoster(queryClient),
  })
}
